import { createSignal } from "solid-js";

import { closestSelector } from "@/lib/gestures/guards";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { TODO_ROW_SWIPE } from "@/lib/gestures/presets";

interface HabitSwipeGesturesOptions {
  /** Swipe right past the commit threshold: mark today done (no-op if already done). */
  onComplete: () => void;
  /** Swipe left past the commit threshold: toggle the freeze/skip state for today. */
  onFreeze: () => void;
  getSurfaceRef: () => HTMLDivElement | undefined;
}

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_VIBRATION_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
} = TODO_ROW_SWIPE;

/**
 * Swipe-to-action for a habit row in the today-view.
 *
 * Same standard as the canon todo-row swipe (`use-todo-swipe-gestures`): the
 * shared recognizer owns the deadzone, axis-lock, rubber-band and rising-edge
 * commit haptic; this hook only maps the two commit directions to the row's
 * primary actions and animates the row off-screen on commit before bouncing back.
 *
 *   - Right → complete today (mirrors the toggle button).
 *   - Left  → freeze/skip today (mirrors the snowflake button).
 *
 * Drags that begin on a `<button>`/`<a>` are ignored so the one-tap toggle and
 * the secondary action buttons keep working unchanged.
 */
export function useHabitSwipeGestures(options: HabitSwipeGesturesOptions) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let hasSwiped = false;

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_VIBRATION_MS,
    canStart: ({ target }) => !closestSelector(target, 'button, a, [data-no-habit-swipe="true"]'),
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      setTx(applyRubberBand(sample.dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: () => {
      hasSwiped = true;
      setIsSwiping(false);
      setIsAnimating(true);

      const finalTx = tx();
      const surfaceRef = options.getSurfaceRef();
      const width = surfaceRef?.clientWidth ?? FALLBACK_WIDTH_PX;

      if (finalTx > COMMIT_THRESHOLD_PX) {
        // Swipe right: complete today.
        setTx(width);
        setTimeout(() => {
          options.onComplete();
          setTx(0);
          setIsAnimating(false);
        }, COMMIT_ANIMATION_MS);
      } else if (finalTx < -COMMIT_THRESHOLD_PX) {
        // Swipe left: freeze/skip today.
        setTx(-width);
        setTimeout(() => {
          options.onFreeze();
          setTx(0);
          setIsAnimating(false);
        }, COMMIT_ANIMATION_MS);
      } else {
        // Cancel and bounce back to center.
        setTx(0);
        setTimeout(() => {
          setIsAnimating(false);
        }, CANCEL_ANIMATION_MS);
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      setTx(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, CANCEL_ANIMATION_MS);
    },
  });

  const base = pointerHandlers(recognizer);

  return {
    tx,
    isSwiping,
    isAnimating,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
    getHasSwiped: () => hasSwiped,
    resetHasSwiped: () => {
      hasSwiped = false;
    },
    COMMIT_THRESHOLD_PX,
  };
}
